import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { api } from '@/lib/api';
import { TID } from '@/lib/testIds';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, X } from 'lucide-react';

const empty = { name: '', slug: '', sort_order: 0, active: true, description: '' };

export default function AdminCategoriesPage() {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get('/menu/categories', { params: { active_only: false } });
    setCats(data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/admin/categories/${editing.id}`, editing);
        toast.success('Category updated');
      } else {
        await api.post('/admin/categories', editing);
        toast.success('Category created');
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      const { data } = await api.delete(`/admin/categories/${c.id}`);
      if (data.deactivated) toast.message('Category had items — deactivated instead.');
      else toast.success('Category deleted');
      await load();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <AdminLayout>
      <div data-testid={TID.admin.categoriesPage}>
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-display text-3xl text-white">CATEGORIES</h1>
          <button onClick={() => setEditing({ ...empty })} className="h-10 px-4 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold inline-flex items-center gap-2"><Plus size={16} />New category</button>
        </div>
        <p className="text-sm text-white/65 mb-5">Group your menu items. Drag-free sort order via the order field.</p>

        <div className="sb-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-white/55 bg-black/30">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Slug</th>
                <th className="text-left px-4 py-3">Description</th>
                <th className="text-left px-4 py-3">Sort</th>
                <th className="text-left px-4 py-3">Active</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cats.map((c) => (
                <tr key={c.id} className="border-t border-[hsl(var(--sb-border))]">
                  <td className="px-4 py-3 text-white">{c.name}</td>
                  <td className="px-4 py-3 text-white/70">{c.slug}</td>
                  <td className="px-4 py-3 text-white/70 max-w-md truncate">{c.description}</td>
                  <td className="px-4 py-3 text-white/85">{c.sort_order}</td>
                  <td className="px-4 py-3">{c.active ? <span className="text-[hsl(var(--sb-green))]">Yes</span> : <span className="text-[hsl(var(--sb-red))]">No</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing({ ...c })} className="h-8 w-8 grid place-items-center rounded-md hover:bg-white/5 text-white/80 inline-flex"><Pencil size={14} /></button>
                    <button onClick={() => remove(c)} className="h-8 w-8 grid place-items-center rounded-md hover:bg-white/5 text-[hsl(var(--sb-red))] inline-flex ml-1"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {!cats.length && <tr><td colSpan={6} className="py-10 text-center text-white/55">No categories.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md bg-[hsl(var(--sb-surface))] h-full overflow-y-auto border-l border-[hsl(var(--sb-border))]" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[hsl(var(--sb-border))] flex items-center justify-between">
              <div className="font-display text-2xl text-white">{editing.id ? 'EDIT CATEGORY' : 'NEW CATEGORY'}</div>
              <button onClick={() => setEditing(null)} className="h-9 w-9 grid place-items-center text-white/70"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <F label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              <F label="Slug (optional)" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
              <F label="Description" textarea value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />
              <F label="Sort order" type="number" value={editing.sort_order} onChange={(v) => setEditing({ ...editing, sort_order: parseInt(v, 10) || 0 })} />
              <label className="inline-flex items-center gap-2 text-sm text-white/85">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active
              </label>
              <div className="flex gap-3 pt-2">
                <button onClick={save} className="flex-1 h-11 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold">{editing.id ? 'Save' : 'Create'}</button>
                <button onClick={() => setEditing(null)} className="h-11 px-4 rounded-md border border-[hsl(var(--sb-border))] text-white">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const F = ({ label, value, onChange, type = 'text', textarea }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-white/55">{label}</span>
    {textarea ? (
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} className="mt-1 w-full p-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white" />
    ) : (
      <input type={type} value={value === null || value === undefined ? '' : value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white" />
    )}
  </label>
);
